import { CommonModule, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ExtendedModule, FlexModule } from '@angular/flex-layout';
import {
	AbstractControl,
	AsyncValidatorFn,
	FormControl,
	FormsModule,
	ReactiveFormsModule,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router, ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { AccountService } from 'src/app/pages/account/services/account.service';
import { CategoryService } from 'src/app/pages/fundraiser/services/category.service';
import { FundraiserService } from 'src/app/pages/fundraiser/services/fundraiser.service';
import { categories } from 'src/app/pages/search/components/search/category-data';
import { Tools } from 'src/utilities/tools';
import { Location } from '@angular/common';
import { BankService } from 'src/app/pages/user/profile/services/bank.service';

@Component({
	selector: 'app-start-fundraiser-page',
	templateUrl: './start-fundraiser-page.component.html',
	styleUrls: ['./start-fundraiser-page.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		MatCardModule,
		NgFor,
		NgClass,
		NgIf,
		MatFormFieldModule,
		MatChipsModule,
		MatAutocompleteModule,
		MatOptionModule,
		FormsModule,
		ReactiveFormsModule,
		MatSelectModule,
		MatButtonModule,
		ExtendedModule,
		FlexModule,
		MatInputModule,
		MatIconModule,
		MatDividerModule,
	],
})
export class StartFundraiserPageComponent implements OnInit {
	readonly _categories = categories;
	startFundraiserForm!: UntypedFormGroup;
	currentStep: number = 1;
	locations: any = [];
	selectedCategory: any = null;
	selectedBeneficiary: string | null = null;
	previousStep: number = -1;
	private locationSearch$ = new Subject<string>();
	selectedLanguage: FormControl = new FormControl(
		this.accountService.getLocaleId()
	);
	public currentLanguageCode: string = '';
	isStripeCreated: string | null = null;
	// Language codes and icons
	languagecodes: any = [
		{
			id: 0,
			languageName: $localize`:@@languageChooser_en_button:EN - English`,
			language_code: 'en',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'en' +
				'/public',
		},
		{
			id: 1,
			languageName: $localize`:@@languageChooser_nl_button:NL - Nederlands`,
			language_code: 'nl',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'nl' +
				'/public',
		},
		{
			id: 2,
			languageName: $localize`:@@languageChooser_es_button:ES - Español`,
			language_code: 'es',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'es' +
				'/public',
		},
		{
			id: 3,
			languageName: $localize`:@@languageChooser_de_button:DE - Deutsch`,
			language_code: 'de',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'de' +
				'/public',
		},
		{
			id: 4,
			languageName: $localize`:@@languageChooser_fr_button:FR - Français`,
			language_code: 'fr',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'fr' +
				'/public',
		},
		{
			id: 5,
			languageName: $localize`:@@languageChooser_bg_button:BG - Bulgarian`,
			language_code: 'bg',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'bg' +
				'/public',
		},
		{
			id: 6,
			languageName: $localize`:@@languageChooser_hr_button:HR - Croatian`,
			language_code: 'hr',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'hr' +
				'/public',
		},
		{
			id: 7,
			languageName: $localize`:@@languageChooser_cs_button:CS - Czech`,
			language_code: 'cs',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'cs' +
				'/public',
		},
		{
			id: 8,
			languageName: $localize`:@@languageChooser_da_button:DA - Danish`,
			language_code: 'da',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'da' +
				'/public',
		},
		{
			id: 9,
			languageName: $localize`:@@languageChooser_fi_button:FI - Finnish`,
			language_code: 'fi',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'fi' +
				'/public',
		},
		{
			id: 10,
			languageName: $localize`:@@languageChooser_el_button:EL - Greek`,
			language_code: 'el',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'el' +
				'/public',
		},
		{
			id: 11,
			languageName: $localize`:@@languageChooser_hu_button:HU - Hungarian`,
			language_code: 'hu',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'hu' +
				'/public',
		},
		{
			id: 12,
			languageName: $localize`:@@languageChooser_it_button:IT - Italian`,
			language_code: 'it',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'it' +
				'/public',
		},
		{
			id: 13,
			languageName: $localize`:@@languageChooser_pl_button:PL - Polish`,
			language_code: 'pl',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'pl' +
				'/public',
		},
		{
			id: 14,
			languageName: $localize`:@@languageChooser_pt_button:PT - Portuguese`,
			language_code: 'pt',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'pt' +
				'/public',
		},
		{
			id: 15,
			languageName: $localize`:@@languageChooser_ro_button:RO - Romanian`,
			language_code: 'ro',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'ro' +
				'/public',
		},
		{
			id: 16,
			languageName: $localize`:@@languageChooser_sk_button:SK - Slovak`,
			language_code: 'sk',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'sk' +
				'/public',
		},
		{
			id: 17,
			languageName: $localize`:@@languageChooser_sv_button:SV - Swedish`,
			language_code: 'sv',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'sv' +
				'/public',
		},
		{
			id: 18,
			languageName: $localize`:@@languageChooser_uk_button:UK - Ukranian`,
			language_code: 'uk',
			languageIcon:
				'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/' +
				'uk' +
				'/public',
		},
	];

	constructor(
		public categoryService: CategoryService,
		public fundraiserService: FundraiserService,
		public accountService: AccountService,
		private _formBuilder: UntypedFormBuilder,
		public router: Router,
		private location: Location,
		private route: ActivatedRoute,
		private _bankService: BankService
	) {
		this.startFundraiserForm = new UntypedFormGroup({
			category: new UntypedFormControl('', {
				validators: [Validators.required],
			}),
			beneficiary: new UntypedFormControl('', {
				validators: [Validators.required],
			}),
			location: new UntypedFormControl('', {
				validators: [Validators.required],
				asyncValidators: [this.isLocationValid()],
			}),
			name: new UntypedFormControl('', [
				Validators.required,
				Validators.minLength(1),
				Validators.maxLength(75),
				Validators.pattern(
					/^[^\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{1FAC0}-\u{1FAFF}\u{1FB00}-\u{1FBFF}\u{1FC00}-\u{1FCFF}\u{1FD00}-\u{1FDFF}\u{1FE00}-\u{1FEFF}\u{1FF00}-\u{1FFFF}]*$/u
				),
				(control) => {
					// Custom validator to ensure the field is not just whitespace
					const value = control.value || '';
					if (/^\s*$/.test(value)) {
						return { whitespace: true };
					}
					return null;
				},
			]),
		});
	}

	ngOnInit(): void {
		this.prefillFormFromStorage();

		this.route.queryParams.subscribe((params) => {
			const step = +params['step'];

			// Handle incoming user data (e.g., from another component)
			const keysToCheck = [
				'first_name',
				'last_name',
				'email',
				'category',
				'location_locale',
				'beneficiary',
				'language_code',
				'name',
			];

			const hasUserParams = keysToCheck.some((key) => key in params);

			if (hasUserParams) {
				const stored = localStorage.getItem('start_fundraiser');
				let data = stored ? JSON.parse(stored) : {};

				keysToCheck.forEach((key) => {
					if (params[key] !== undefined) {
						let value: any = params[key];
						if (key === 'category') value = +value || value;
						data[key] = value;
					}
				});

				localStorage.setItem('start_fundraiser', JSON.stringify(data));

				// Pre-fetch and validate location BEFORE patching
				if (data.location_locale) {
					this.categoryService
						.getLocation(data.location_locale)
						.subscribe((result) => {
							this.locations =
								JSON.parse(JSON.stringify(result))['data']['result'] || [];

							this.startFundraiserForm.patchValue({
								category: data.category || '',
								beneficiary: data.beneficiary || '',
								location: data.location_locale || '',
								name: data.name || '',
							});

							this.startFundraiserForm
								.get('location')
								?.updateValueAndValidity();
						});
					this.router.navigate([], {
						relativeTo: this.route,
						queryParams: {},
						replaceUrl: true,
					});
				} else {
					this.startFundraiserForm.patchValue({
						category: data.category || '',
						beneficiary: data.beneficiary || '',
						location: '',
						name: data.name || '',
					});
					this.router.navigate([], {
						relativeTo: this.route,
						queryParams: {},
						replaceUrl: true,
					});
				}

				this.selectedCategory = data.category;
				this.selectedBeneficiary = data.beneficiary;
				this.selectedLanguage.setValue(data.language_code);
			}

			// Step navigation
			if (step === 2) {
				this.currentStep = 2;
				// Clear query params from URL without reloading
				this.router.navigate([], {
					relativeTo: this.route,
					queryParams: {},
					replaceUrl: true,
				});
			} else {
				// If no valid query param, check localStorage for valid step 2 data
				const existingData = localStorage.getItem('start_fundraiser');
				if (existingData) {
					try {
						const parsedData = JSON.parse(existingData);
						if (
							parsedData?.category &&
							parsedData?.location_locale &&
							parsedData?.language_code
						) {
							this.currentStep = 2;
						}
					} catch (e) {
						console.error(
							'Failed to parse start_fundraiser from localStorage',
							e
						);
					}
				}
			}
		});

		// Debounce + search
		this.locationSearch$
			.pipe(debounceTime(600), distinctUntilChanged())
			.subscribe((searchTerm) => {
				this.searchPlaces(searchTerm);
			});

		this.currentLanguageCode = this.accountService.getLocaleId();

		// Init storage if not present
		if (!localStorage.getItem('start_fundraiser')) {
			localStorage.setItem(
				'start_fundraiser',
				JSON.stringify({
					category: null,
					beneficiary: null,
					name: '',
					location_locale: '',
					language_code: this.selectedLanguage.value,
					first_name: '',
					last_name: '',
					email: '',
				})
			);
		}

		this.listenToFormChanges();

		// Beneficiary-driven validator updates
		this.startFundraiserForm
			.get('beneficiary')
			?.valueChanges.subscribe((beneficiary) => {
				const nameControl = this.startFundraiserForm.get('name');
				if (beneficiary === 'yourself') {
					nameControl?.clearValidators();
				} else {
					nameControl?.setValidators([
						Validators.required,
						Validators.minLength(1),
						Validators.maxLength(75),
						Validators.pattern(
							/^[^\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{1FAC0}-\u{1FAFF}\u{1FB00}-\u{1FBFF}\u{1FC00}-\u{1FCFF}\u{1FD00}-\u{1FDFF}\u{1FE00}-\u{1FEFF}\u{1FF00}-\u{1FFFF}]*$/u
						),
						(control) => {
							const value = control.value || '';
							return /^\s*$/.test(value) ? { whitespace: true } : null;
						},
					]);
				}
				nameControl?.updateValueAndValidity();
			});
	}

	// New helper function to process query parameters
	processQueryParams(params: any, step: number | null): void {
		// Update local storage with query params
		const existingData = localStorage.getItem('start_fundraiser');
		let data = existingData ? JSON.parse(existingData) : {};
		data = {
			...data,
			first_name: params.first_name,
			last_name: params.last_name,
			email: params.email,
			category: params.category,
			location_locale: params.location_locale,
			beneficiary: params.beneficiary,
			language_code: params.language_code,
			name: data.name || '', // Preserve existing name if not provided
		};
		localStorage.setItem('start_fundraiser', JSON.stringify(data));

		// Update form and component state
		if (params.location_locale) {
			this.categoryService
				.getLocation(params.location_locale)
				.subscribe((result) => {
					this.locations =
						JSON.parse(JSON.stringify(result))['data']['result'] || [];
					this.startFundraiserForm.patchValue({
						category: params.category || '',
						beneficiary: params.beneficiary || '',
						location: params.location_locale || '',
						name: data.name || '',
					});
					this.selectedLanguage.setValue(params.language_code);
					this.selectedCategory = params.category;
					this.selectedBeneficiary = params.beneficiary || '';
					if (step === 2 || this.checkStepOneCompletion()) {
						this.currentStep = 2;
					}
					// Clear query params
					this.router.navigate([], {
						relativeTo: this.route,
						queryParams: {},
						replaceUrl: true,
					});
				});
		} else {
			this.startFundraiserForm.patchValue({
				category: params.category || '',
				beneficiary: params.beneficiary || '',
				location: '',
				name: data.name || '',
			});
			this.selectedLanguage.setValue(params.language_code);
			this.selectedCategory = params.category;
			this.selectedBeneficiary = params.beneficiary || '';
			if (step === 2 || this.checkStepOneCompletion()) {
				this.currentStep = 2;
			}
			// Clear query params
			this.router.navigate([], {
				relativeTo: this.route,
				queryParams: {},
				replaceUrl: true,
			});
		}
	}
	listenToFormChanges(): void {
		const formControls = this.startFundraiserForm.controls;

		Object.keys(formControls).forEach((key) => {
			formControls[key].valueChanges
				.pipe(debounceTime(300), distinctUntilChanged())
				.subscribe(() => {
					const control = formControls[key];

					if (control.valid) {
						this.updateLocalStorageField(key, control.value);
					}
				});
		});

		this.selectedLanguage.valueChanges
			.pipe(distinctUntilChanged())
			.subscribe((value) => {
				this.updateLocalStorageField('language_code', value);
			});
	}

	ngAfterViewInit(): void {
		if (window.innerWidth <= 599) {
			// Wait for a tick to ensure everything is rendered
			setTimeout(() => this.scrollToOffsetTop(), 0);
		}
	}

	updateLocalStorageField(field: string, value: any): void {
		const existingData = localStorage.getItem('start_fundraiser');
		if (existingData) {
			const data = JSON.parse(existingData);

			// Preserve user fields if already present
			const preservedFields = {
				first_name: data.first_name || '',
				last_name: data.last_name || '',
				email: data.email || '',
			};

			// Update the modified field
			if (field === 'location') {
				data['location_locale'] = value;
			} else {
				data[field] = value;
			}

			localStorage.setItem(
				'start_fundraiser',
				JSON.stringify({ ...data, ...preservedFields })
			);
		}
	}

	prefillFormFromStorage(): void {
		const storedData = localStorage.getItem('start_fundraiser');
		if (storedData) {
			const data = JSON.parse(storedData);
			if (data.location_locale) {
				this.categoryService
					.getLocation(data.location_locale)
					.subscribe((result) => {
						this.locations =
							JSON.parse(JSON.stringify(result))['data']['result'] || [];
						this.startFundraiserForm.patchValue({
							category: data.category || '',
							beneficiary: data.beneficiary || '',
							location: data.location_locale || '',
							name: data.name || '',
						});
					});
			} else {
				this.startFundraiserForm.patchValue({
					category: data.category || '',
					beneficiary: data.beneficiary || '',
					location: '',
					name: data.name || '',
				});
			}
			this.selectedLanguage.setValue(
				data.language_code || this.selectedLanguage.value
			);
			this.selectedCategory = data.category;
			this.selectedBeneficiary = data.beneficiary || '';
			setTimeout(() => {
				if (this.checkStepOneCompletion()) {
					this.goToNextStep();
				}
			}, 500);
		}
	}

	ngDoCheck(): void {}

	onNameInput() {
		this.startFundraiserForm.get('location')?.markAllAsTouched();
	}

	onLocationInput(term: string): void {
		this.locationSearch$.next(term); // Pass the input value to the subject
	}

	searchPlaces(term: string): void {
		if (term.trim()) {
			this.categoryService.getLocation(term).subscribe(
				(result) => {
					this.locations = this.locations =
						JSON.parse(JSON.stringify(result))['data']['result'] || [];
				},
				(error) => {
					// Handle the error, mark the form as dirty
					this.startFundraiserForm?.controls.location.markAsDirty({
						onlySelf: true,
					});
				}
			);
		}
	}

	isLocationValid(): AsyncValidatorFn {
		return (control: AbstractControl) => {
			const validationPromise: Promise<ValidationErrors | null> = new Promise(
				(resolve, reject) => {
					let value = control.value;
					let doesExist: boolean = false;
					if (this.locations?.length > 0) {
						this.locations.forEach((location: any) => {
							if (location['description'] == value) {
								doesExist = true;
							}
						});
					}

					if (doesExist) {
						resolve(null);
					} else {
						resolve({ location: 'doNotExist' });
					}
				}
			);
			return validationPromise;
		};
	}

	onBeneficiarySelect(beneficiaryType: string) {
		this.selectedBeneficiary = beneficiaryType;
		if (beneficiaryType === 'yourself') {
			this.startFundraiserForm.get('name')?.setValue('');
		}

		this.startFundraiserForm
			.get('beneficiary')
			?.setValue(this.selectedBeneficiary);
	}

	selectCategory(category: any): void {
		this.selectedCategory = category.id;
		this.startFundraiserForm.get('category')?.setValue(this.selectedCategory);
	}

	isSelected(category: any): boolean {
		return this.selectedCategory === category.id;
	}

	save() {
		let location_local: string = '';

		this.locations.forEach((location: any) => {
			if (
				location['description'] ==
				this.startFundraiserForm.get('location')?.value
			) {
				location_local =
					location['description'] ||
					this.startFundraiserForm.get('location')?.value ||
					'';
			}
		});

		// Get existing data from localStorage (if any)
		const existingDataString = localStorage.getItem('start_fundraiser');
		let existingData = existingDataString ? JSON.parse(existingDataString) : {};

		// Merge with new/updated fields from startFundraiser form
		const startFundraiser = {
			...existingData, // Keep existing fields
			category: this.selectedCategory,
			beneficiary: this.selectedBeneficiary,
			name: this.startFundraiserForm.get('name')?.value || '',
			location_locale: location_local || '',
			language_code: this.selectedLanguage.value,
			first_name: existingData.first_name || '',
			last_name: existingData.last_name || '',
			email: existingData.email || '',
		};

		// Save merged object back to localStorage
		localStorage.setItem('start_fundraiser', JSON.stringify(startFundraiser));

		this._bankService.getStripeStatus(true).subscribe(
			(res: any) => {
				this.isStripeCreated = res?.data?.status;
				if (
					this.isStripeCreated === 'created' &&
					res?.data?.charges_enabled === false
				) {
					this.router.navigate(['fundraising/country']);
					return;
				}
			},
			(error: any) => {
				console.error('Error fetching Stripe status:', error);
			}
		);

		// Navigate as before
		let createUrl = 'fundraising/create';
		Tools.setPreviousPath(createUrl);

		const isLoggedIn = this.accountService.isLoggedIn.getValue();
		if (isLoggedIn) {
			this.router.navigate([createUrl]);
		} else {
			this.router.navigate(['account/register']);
		}
	}

	checkStepOneCompletion(): boolean {
		const form = this.startFundraiserForm;
		const categoryValid = form.get('category')?.valid ?? false;
		const locationValid = form.get('location')?.valid ?? false;
		const languageSelected = !!this.selectedLanguage.value;

		return categoryValid && locationValid && languageSelected;
	}

	goToNextStep(): void {
		this.currentStep = 2; // Move from step 0 to step 1
		this.scrollToOffsetTop();
	}

	goToStep(step: number): void {
		this.currentStep = step;
		this.scrollToOffsetTop();
	}

	browseFundraiser() {
		window.open(
			this.router.serializeUrl(
				this.router.createUrlTree([`${this.currentLanguageCode}/search`])
			),
			'_blank'
		);
	}

	scrollToOffsetTop(offset: number = 120): void {
		if (window.innerWidth <= 599) {
			window.scrollTo({
				top: offset,
				behavior: 'smooth',
			});
		}
	}
}
